import { lazy, Suspense } from "react";
import type { ComponentProps } from "react";
import type { PhoneNumberInput as PhoneNumberInputImpl } from "./phone-number-input";
import type { PhoneVariant } from "./country-select";
import { cn } from "../../lib/utils";

export type { PhoneVariant } from "./country-select";

// Code-split: libphonenumber-js + the registry load only on screens that
// actually render a phone field (auth / join), not in the main app bundle.
const Impl = lazy(() =>
  import("./phone-number-input").then((m) => ({ default: m.PhoneNumberInput }))
);

type Props = ComponentProps<typeof PhoneNumberInputImpl>;

const skeletonTone: Record<PhoneVariant, string> = {
  dark: "bg-white/5 border-primary/20",
  light: "bg-white border-[#8d6b5e]/20",
  theme: "bg-transparent border-current/20",
};

function Skeleton({ variant = "light" }: { variant?: PhoneVariant }) {
  const tone = skeletonTone[variant];
  return (
    <div className="space-y-1.5" aria-hidden="true">
      <div className="flex items-stretch gap-2">
        <div className={cn("h-[52px] w-20 animate-pulse rounded-lg border", tone)} />
        <div className={cn("h-[52px] flex-1 animate-pulse rounded-lg border", tone)} />
      </div>
      <div className="h-4" />
    </div>
  );
}

/** Lazy, Suspense-wrapped phone input with a layout-stable fallback. */
export function PhoneNumberInput(props: Props) {
  return (
    <Suspense fallback={<Skeleton variant={props.variant} />}>
      <Impl {...props} />
    </Suspense>
  );
}
